import type { ProductVisit, ProductVisitRecord } from "@reco/service/src/types/records";
import type { Recommendation } from "@reco/shared/src/types/recommendation";

import { HISTORY_DB_KEY } from "@reco/service/src/constants";
import { getVisitHistory } from "@reco/service/src/utils/storage";
import { Scenario } from "@reco/shared/src/constants";

export default class LocalRecommendations {
  private static readonly MAX_HISTORY_SECONDS = 365 * 24 * 60 * 60; // one year
  private static readonly MAX_HISTORY_LENGTH = 15;

  public static isProductsScenario(record: ProductVisitRecord) {
    return !record.params.scenario || record.params.scenario === Scenario.PRODUCTS;
  }

  public static isCrossScenario(record: ProductVisitRecord) {
    return record.params?.scenario === Scenario.CROSS;
  }

  public getCart(limit: number, contextId?: string, exclude?: string[], scenario?: Scenario): Recommendation[] {
    if (!contextId) {
      return [];
    }

    const cartErpNumbers = contextId.split(",").filter((num) => num.trim());

    const visitHistory = getVisitHistory();
    const isNotPartOfExcludedProducts = (record: Recommendation) => !(exclude || []).includes(record.itemId);

    const isBasketProduct = (record: ProductVisitRecord) => cartErpNumbers.includes(record.params.contextProduct);

    const flatRecommendations = (acc, record: ProductVisitRecord) => {
      return acc.concat(record.response.recommendationItems);
    };
    const calculateUniqueRecommendationsWithAverageScore = (acc, record: Recommendation) => {
      const prevRecord = acc.find((prevRecords) => prevRecords.itemId === record.itemId);

      if (!prevRecord) {
        acc.push({
          itemId: record.itemId,
          relevance: record.relevance,
          count: 1,
        });
        return acc;
      }

      prevRecord.relevance = (prevRecord.count * prevRecord.relevance + record.relevance) / ++prevRecord.count;

      return acc;
    };

    const orderByOverlapCountAndRelevanceDesc = (record1: Recommendation, record2: Recommendation) => {
      if ((record1.count ?? 0) - (record2.count ?? 0) < 0) {
        return 1;
      }
      if ((record1.count ?? 0) - (record2.count ?? 0) > 0) {
        return -1;
      }

      if (record1.relevance - record2.relevance < 0) {
        return 1;
      }
      if (record1.relevance - record2.relevance > 0) {
        return -1;
      }
      return 0;
    };

    return visitHistory
      .filter(
        scenario === Scenario.PRODUCTS ? LocalRecommendations.isProductsScenario : LocalRecommendations.isCrossScenario,
      )
      .filter(isBasketProduct)
      .reduce(flatRecommendations, [])
      .filter(isNotPartOfExcludedProducts)
      .reduce(calculateUniqueRecommendationsWithAverageScore, [])
      .sort(orderByOverlapCountAndRelevanceDesc)
      .slice(0, limit);
  }

  public getLastSeen(limit: number, contextId?: string, excludedProducts?: string[]): Recommendation[] {
    const visitHistory: ProductVisitRecord[] = getVisitHistory();
    const finalExcludedProducts = contextId ? [contextId].concat(excludedProducts ?? []) : (excludedProducts ?? []);

    const isNotPartOfExcludedProducts = (record: ProductVisitRecord) =>
      !finalExcludedProducts.includes(record.params.contextProduct);

    const isNotRetail = (record: ProductVisitRecord) => record.params.tradeType !== "retail";

    // relevance should be between 0 and 1
    const toRecommendationFormat = (record: ProductVisitRecord, index: number): Recommendation => {
      return {
        itemId: record.params.contextProduct || "",
        relevance: 0.95 - index * 0.05,
      };
    };
    return visitHistory
      .filter(LocalRecommendations.isProductsScenario)
      .filter(isNotPartOfExcludedProducts)
      .filter(isNotRetail)
      .slice(0, limit)
      .map(toRecommendationFormat);
  }

  public getPersonalization(limit: number, contextId?: string, exclude?: string[]): Recommendation[] {
    const visitHistory = getVisitHistory();
    const lastSeen = visitHistory.map((record: ProductVisitRecord) => record.params.contextProduct);
    const excludeProducts = exclude ? exclude.concat(lastSeen) : lastSeen;
    const allExcludedProducts = contextId ? [contextId].concat(excludeProducts) : excludeProducts;

    const isNotPartOfExcludedProducts = (record: Recommendation) => allExcludedProducts.indexOf(record.itemId) === -1;

    const noDuplicateProducts = (record: Recommendation, index: number, arr) => {
      for (let i = 0; i < index; i++) {
        if (arr[i].itemId === record.itemId) {
          return false;
        }
      }
      return true;
    };

    const normalizeRelevanceScore = (record: ProductVisitRecord, index: number) => {
      // relevance x (1 - time discount); timeDiscount = 0.05 * index
      const timeDiscount = 0.05 * index;
      record.response.recommendationItems = record.response.recommendationItems.map((item: Recommendation) => {
        const normalizedRelevance = item.relevance * (1 - timeDiscount);
        return {
          itemId: item.itemId,
          relevance: normalizedRelevance,
        };
      });
      return record;
    };

    const flatRecommendations = (acc, record: ProductVisitRecord) => {
      return acc.concat(record.response.recommendationItems);
    };

    const orderByRelevanceDesc = (record1: Recommendation, record2: Recommendation) => {
      if (record1.relevance - record2.relevance < 0) {
        return 1;
      }
      if (record1.relevance - record2.relevance > 0) {
        return -1;
      }
      return 0;
    };

    return visitHistory
      .filter(LocalRecommendations.isProductsScenario)
      .map(normalizeRelevanceScore)
      .reduce(flatRecommendations, [])
      .filter(isNotPartOfExcludedProducts)
      .filter(noDuplicateProducts)
      .sort(orderByRelevanceDesc)
      .slice(0, limit);
  }

  public saveVisit(visit: ProductVisit) {
    if (!visit.contextId || !visit.recommendationItems) {
      return;
    }

    const timestamp = Math.floor(Date.now() / 1000); // seconds
    // just save the minimal amount of required data
    const recommendation: ProductVisitRecord = {
      timestamp,
      params: {
        contextProduct: visit.contextId,
        scenario: visit.scenario,
        categoryPath: visit.categoryPath || undefined,
        categoryPathName: visit.categoryPathName || undefined,
        brand: visit.brand,
        brandName: visit.brandName,
        tradeType: window.unified_datalayer_product?.tradeType || undefined,
      },
      response: {
        recommendationItems: visit.recommendationItems,
      },
    };

    // only recos that did not expire until now
    const isExpired = (record: ProductVisitRecord) => {
      return record.timestamp + LocalRecommendations.MAX_HISTORY_SECONDS > timestamp;
    };

    const removeDuplicates = (record: ProductVisitRecord, index: number, arr) => {
      for (let i = 0; i < index; i++) {
        if (arr[i].params.contextProduct === record.params.contextProduct) {
          return false;
        }
      }
      return true;
    };

    const visitHistory = getVisitHistory();
    const newVisitHistory = [recommendation, ...visitHistory]
      .filter(isExpired)
      .filter(removeDuplicates)
      .slice(0, LocalRecommendations.MAX_HISTORY_LENGTH);

    localStorage.setItem(HISTORY_DB_KEY, JSON.stringify(newVisitHistory));
  }
}
